import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Default sections
import { CrossVariableGrossStructure } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { getCrossStoreLabel, getFinanceCommissionsContent } from "./utils";

// Layouts
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Custom components
import CommissionContent from "./CommissionContent";

// MUI
import { Grid } from "@mui/material";

// Constants
const GLOBAL_UNITS_LABEL = "Global gross and units counts";
const potentialKeys = ["newVehicles", "preOwned", "finance"];
const excludeFromTopSwitches = ["finance"];

const getIsValid = (errors, sectionId) => {
	if (!errors?.get(sectionId)) {
		return true;
	}
	return !errors?.get(sectionId)?.length;
};

const getPotentialTabsErrorsKeys = (section, tabs = []) => tabs?.map((tab) => getErrorKey(section, tab?.key));

const getErrorsToRemove = (errors = [], errorsToKeep = []) => {
	if (errors.length) {
		return errors.filter((key) => !errorsToKeep.includes(key));
	}
	return [];
};

const getTopSwitches = (data = null, args = null) => {
	const { readOnly = false, isActiveFinance = false, onUpdateSwitch = () => {} } = args || {};

	if (isActiveFinance) {
		return [];
	}

	const { content = null } = data || {};
	const { globalUnitCounts = false } = content || {};

	return [
		<CustomizedSwitches
			key={"globalUnitCounts"}
			textLeft={true}
			textChecked={GLOBAL_UNITS_LABEL}
			textUnChecked={GLOBAL_UNITS_LABEL}
			checked={globalUnitCounts}
			disabled={readOnly}
			onChange={(value) => onUpdateSwitch("globalUnitCounts", value)}
			tooltip={{
				title: "Uses all units of New and Pre-Owned Vehicles together.",
				placement: "bottom",
				key: "default-commissions-global-unit-counts-tooltip",
			}}
		/>,
	];
};

const getBottomSwitches = (data = null, args = null) => {
	const { area = "", readOnly = false, isActiveFinance = false, onUpdateSwitch = () => {} } = args || {};

	const { content = null } = data || {};
	const section = content?.[area]?.["commissions"] || {};

	const { applyTiersRetroactively = false, individual = false } = section || {};

	if (isActiveFinance) {
		const { globalUnitCounts = false } = content?.[area] || {};
		return [
			<CustomizedSwitches
				key={"globalUnitCounts"}
				textLeft={true}
				textChecked={"Global gross and Contracts"}
				textUnChecked={"Global gross and Contracts"}
				checked={globalUnitCounts}
				disabled={readOnly}
				onChange={(value) => onUpdateSwitch(area, "globalUnitCounts", value)}
				tooltip={{
					title: "Uses all contracts of New and Pre-Owned Vehicles together.",
					placement: "bottom",
					key: "default-commissions-global-unit-counts-tooltip",
				}}
			/>,
		];
	}

	return [
		<CustomizedSwitches
			key={"individual"}
			textLeft={true}
			textChecked={"Store"}
			textUnChecked={"Individual"}
			checked={individual}
			disabled={readOnly}
			onChange={(value) => onUpdateSwitch(area, "individual", value)}
		/>,
		<CustomizedSwitches
			key={"applyTiersRetroactively"}
			textLeft={true}
			textChecked={"Apply retroactively"}
			textUnChecked={"Apply retroactively"}
			checked={applyTiersRetroactively}
			disabled={readOnly}
			onChange={(value) => onUpdateSwitch(area, "applyTiersRetroactively", value)}
		/>,
	];
};

const getCommissionsFromModel = (model) => model["content"];
export default function CrossStoreCommissions(props) {
	const {
		sectionId = "crossStoreCommissions",
		content = null,
		brands = {
			newVehicles: [],
			preOwned: [],
		},
		loading = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
		useSectionsErrors = () => {},
	} = props;

	// Hooks
	const { errors, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line no-unused-vars
	const [tabIndex, setTabIndex] = useState(0);
	const [model, setModel] = useState(null);

	// References
	const wasUpdated = useRef(false);
	const saveErrors = useRef([]);

	const financeTabIndex = model?.tabs?.findIndex((tab) => tab?.key === "finance");
	const isActiveFinance = Boolean(financeTabIndex === tabIndex);
	const activeIdentifier = model?.tabs?.[tabIndex]?.key;

	// Error handler
	const tabsErrors = useMemo(() => {
		if (errors.size && model?.tabs) {
			const localErrors = Array.from(Array(model.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabs);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey) || saveErrors?.current?.includes(errorKey);
				localErrors[index] = Boolean(included);
			});
			return localErrors;
		}
		return [];
	}, [sectionId, errors, model]);

	// Actions handlers
	const update = (data, isValid = false) => {
		if (isValid && wasUpdated.current && Boolean(model)) {
			const modelToSave = getCommissionsFromModel(data);
			onActionUpdate?.(sectionId, null, modelToSave, isValid);
		}
		setModel(data);
	};

	const handleUpdateContent = (area, data) => {
		const updatedModel = { ...model };
		const previousData = updatedModel["content"][area] || {};
		const { commissions = null, miniDeal = [], variableGrossStructure = [] } = data || {};
		updatedModel["content"][area] = { ...previousData, commissions, miniDeal, variableGrossStructure };
		wasUpdated.current = true;
		update(updatedModel, !errors.size);
	};

	const handleUpdateSwitch = (section, area, value) => {
		let updatedModel = { ...model };
		const changedFinanceGlobalSwitch = section === "finance" && area === "globalUnitCounts";

		if (!section) {
			updatedModel["content"][area] = value;
		} else if (changedFinanceGlobalSwitch) {
			const prevContent = updatedModel["content"];
			const financeSection = updatedModel["content"][section];
			const contentToReplace = {
				finance: {
					...financeSection,
					globalUnitCounts: value,
					commissions: getFinanceCommissionsContent(value),
					variableGrossStructure: CrossVariableGrossStructure(section),
				},
			};
			updatedModel = { ...updatedModel, content: { ...prevContent, ...contentToReplace } };
			handleValidation(sectionId, section, true);
		} else {
			updatedModel["content"][section]["commissions"][area] = value;
		}

		wasUpdated.current = true;
		update({ ...updatedModel }, !errors.size);
	};

	const handleValidation = (section, key, value) => {
		value && !saveErrors?.current?.includes(key) ? removeSectionError(section, key) : addSectionError(section, key);
	};

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	useEffect(() => {
		setModel({
			content: clone(content),
			tabs: [
				{ key: "newVehicles", tabName: getCrossStoreLabel("newVehicles") },
				{ key: "preOwned", tabName: getCrossStoreLabel("preOwned") },
				{ key: "finance", tabName: getCrossStoreLabel("finance") },
			],
		});
		wasUpdated.current = false;

		return () => {
			setModel(null);
		};
	}, [content]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model) && (
						<TabSelectable
							tabs={model?.tabs?.map((tab) => tab.tabName)}
							onTabChange={(_, information) => setTabIndex(information?.index)}
							errors={tabsErrors}
							content={model?.tabs?.map(({ key: tabIdentifier }, index) => {
								return (
									<Grid container key={`${tabIdentifier}_${index}`}>
										<Grid item xs={12} className={"models-tiered-area"}>
											<CommissionContent
												identifier={tabIdentifier}
												content={model?.["content"]?.[tabIdentifier] ?? null}
												brands={brands?.[tabIdentifier] ?? []}
												errors={Boolean(errors.size)}
												loadingMakes={loadingMakes}
												readOnly={readOnly || loading}
												onActionUpdate={(identifier, data) => handleUpdateContent(identifier, data)}
												onActionValidation={(isValid) => handleValidation(sectionId, tabIdentifier, isValid)}
											/>
										</Grid>
									</Grid>
								);
							})}
							topActions={getTopSwitches(model, {
								readOnly,
								isActiveFinance,
								onUpdateSwitch: (area, value) => handleUpdateSwitch("", area, value),
							})}
							bottomActions={getBottomSwitches(model, {
								readOnly,
								isActiveFinance,
								area: activeIdentifier,
								onUpdateSwitch: (section, area, value) => handleUpdateSwitch(section, area, value),
							})}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
CrossStoreCommissions.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	brands: PropTypes.object,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};
